import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import type { UserDetail } from '../services/types';
import { copyText } from '../utils/copyText';
import { formatDate, formatMoney, subStatusClass, subStatusLabel } from '../utils/format';
import { SupportAvatar } from './SupportAvatar';
import { useToast } from './ToastProvider';

type Props = {
  userId: number | null;
  onClose: () => void;
};

export function UserQuickDrawer({ userId, onClose }: Props) {
  const toast = useToast();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const copyId = async () => {
    const ok = await copyText(String(userId));
    if (ok) toast.success(`ID ${userId} скопирован`);
    else toast.error('Не удалось скопировать ID');
  };

  useEffect(() => {
    if (userId == null) {
      setUser(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUser(null);
    (async () => {
      const res = await api.getUser(userId);
      if (cancelled) return;
      setLoading(false);
      if (res.data) setUser(res.data);
      else setError(res.error?.message ?? 'Не удалось загрузить пользователя');
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (userId == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [userId, onClose]);

  if (userId == null) return null;

  const payments = user?.payments.slice(0, 5) ?? [];
  const keys = user?.vpn_keys.slice(0, 8) ?? [];

  return (
    <div className="user-drawer-root" role="dialog" aria-modal="true" aria-label="Карточка клиента">
      <button type="button" className="user-drawer-backdrop" aria-label="Закрыть" onClick={onClose} />
      <aside className="user-drawer-panel">
        <div className="user-drawer-head">
          <div className="user-drawer-title-row">
            <SupportAvatar
              role="user"
              userId={userId}
              firstName={user?.first_name}
              username={user?.username}
              size="md"
            />
            <div>
              <h3 className="user-drawer-title">
                {user?.first_name || user?.username || `User ${userId}`}
              </h3>
              <p className="muted user-drawer-sub">
                <button type="button" className="user-drawer-id-btn" onClick={() => void copyId()}>
                  ID {userId}
                </button>
                {user?.username ? ` · @${user.username}` : ''}
              </p>
            </div>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            ✕
          </button>
        </div>

        {loading && <p className="muted">Загрузка…</p>}
        {error && <p className="error-text">{error}</p>}

        {user && (
          <div className="user-drawer-body">
            <section className="user-drawer-section">
              <h4>Подписка</h4>
              <div className="user-drawer-grid">
                <div>
                  <span className="muted">Статус</span>
                  <div className={subStatusClass(user.subscription_status)}>
                    {subStatusLabel(user.subscription_status)}
                  </div>
                </div>
                <div>
                  <span className="muted">Тариф</span>
                  <div>{user.subscription_tier ?? '—'}</div>
                </div>
                <div>
                  <span className="muted">До</span>
                  <div>{formatDate(user.subscription_until)}</div>
                </div>
                <div>
                  <span className="muted">Активных ключей</span>
                  <div>{user.active_keys_count}</div>
                </div>
              </div>
            </section>

            <section className="user-drawer-section">
              <h4>Последние платежи</h4>
              {payments.length === 0 ? (
                <p className="muted">Нет платежей</p>
              ) : (
                <ul className="user-drawer-list">
                  {payments.map((p) => (
                    <li key={p.id}>
                      <Link to={`/payments/${encodeURIComponent(p.id)}`} onClick={onClose}>
                        {formatMoney(p.amount)}
                      </Link>
                      <span className="muted">
                        {' '}
                        · {p.status} · {p.period}
                        {p.subscription_tier ? ` · ${p.subscription_tier}` : ''} ·{' '}
                        {formatDate(p.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="user-drawer-section">
              <h4>VPN ключи</h4>
              {keys.length === 0 ? (
                <p className="muted">Нет ключей</p>
              ) : (
                <ul className="user-drawer-list">
                  {keys.map((k) => (
                    <li key={`${k.protocol ?? 'wg'}-${k.id}`}>
                      {(k.protocol ?? 'wg').toUpperCase()} · {k.server_country.toUpperCase()} · #
                      {k.client_id} · {k.status}
                      {k.protocol !== 'ovpn' ? (
                        <>
                          {' '}
                          <Link to={`/vpn/${k.id}`} onClick={onClose}>
                            #{k.id}
                          </Link>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="user-drawer-actions">
              <Link className="btn btn-primary" to={`/users/${userId}`} onClick={onClose}>
                Полная карточка
              </Link>
              <button type="button" className="btn btn-ghost" onClick={() => void copyId()}>
                Скопировать ID
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
